import {
  StyledSelectionContainer,
  StyledPaymentTextContainer,
  StyledTitle,
} from './styles/PaymentSelector.style'
import { PaymentSelectorProps, SelectionType } from './interfaces'
import { ClubbiToggleButtonGroup, InstallmentsToggleButtonGroup } from './ToggleButtonGroup'
import { WalletIcon } from 'clubbi-ui'
import { useEffect, useState } from 'react'

import {
  DEFAULT_DELIVERY_FEE,
  instantPaymentData,
  instantPaymentMessage,
  invoiceTypesData,
  MOCK_DELIVERY_FEE,
  noDeadlineAvailableMessage,
  paymentTypesData,
} from '../../../common/constants'
import { useAppSelector } from '../../../common/hooks'
import {
  getDeadlineMessage,
  getInstallmentsOptions,
  getShippingInfos,
  installmentsMessage,
} from '../../../common/utils'
import { hasTwoDaysItems } from '../../../common/productUtils'
import { getMissionsDiscount } from '../../../common/missions'
import { CartItem } from '../../../store/data/types'
import { getCartItemPrice } from '../../../common/cardUtils'

export const PaymentSelector = ({ type, setFormValue, formValue }: PaymentSelectorProps) => {
  const isPayment = type === SelectionType.PAYMENT
  const [paymentText, setPaymentText] = useState<string | null>(noDeadlineAvailableMessage)

  const session = useAppSelector((state) => state.session)
  const { shippingType } = useAppSelector((state) => state.shippingInfos)
  const { cart, scheduledDelivery, missions } = useAppSelector((state) => state)

  const { isInstantPayment, isScheduleEnabled, city } = session
  const noSelected = formValue[type] === -1

  const cartItems = Object.values(cart).filter((item) => item.quantity > 0)

  const totalPrice = cartItems.reduce(
    (x: number, cartItem: CartItem) => x + getCartItemPrice(cartItem),
    0
  )

  let fee = getShippingInfos(shippingType, isScheduleEnabled!, scheduledDelivery).value

  if (isScheduleEnabled && fee === DEFAULT_DELIVERY_FEE) {
    fee = MOCK_DELIVERY_FEE
  }

  const discountAmount = getMissionsDiscount(missions, cart, fee)
  const totalValue = totalPrice + fee - discountAmount

  const getAdjustedLabels = () => {
    let filteredPaymentTypes = paymentTypesData
    const isBlockedPaymentByCredit = city?.toLowerCase().includes('caxias')

    if (isBlockedPaymentByCredit) {
      filteredPaymentTypes = paymentTypesData.filter((type) => type.label != 'Cartão de crédito')
    }

    if (isPayment) return isInstantPayment ? instantPaymentData : filteredPaymentTypes
    return []
  }

  const handleChange = (event: React.MouseEvent<HTMLElement>, value: string | null) => {
    if (value !== null) {
      const target = event.currentTarget
      const name = target.parentElement?.id || ''

      setFormValue((state) => ({
        ...state,
        [name]: value,
        installments: -1,
        invoiceType: invoiceTypesData[0].id, // Sempre utiliza DANFE
        merchantCodeInvalid: name == 'merchantCode' ? false : state.merchantCodeInvalid,
      }))
    }
  }

  const handleChangeInstallments = (event: React.MouseEvent<HTMLElement>, value: number | null) => {
    if (value !== null) {
      const target = event.currentTarget
      const name = target.parentElement?.id || ''

      setFormValue((state) => ({
        ...state,
        installments: value,
        merchantCodeInvalid: name == 'merchantCode' ? false : state.merchantCodeInvalid,
      }))
    }
  }

  const hasTwoDaysProducts = hasTwoDaysItems(cartItems)

  const isPaymentInstallmentEnabled = session.isPaymentInstallmentEnabled == true

  const noSelectedInstallments = formValue.installments === -1

  const installmentOptions = isPaymentInstallmentEnabled
    ? getInstallmentsOptions(
        session.paymentDeadline,
        formValue.paymentType,
        session.isInstantPayment
      )
    : []

  const showInstallments =
    isPayment && installmentOptions.length > 0 && !noSelected && isPaymentInstallmentEnabled

  useEffect(() => {
    if (isInstantPayment) {
      setPaymentText(instantPaymentMessage)
      return
    }

    const isPixOrBoleto = formValue.paymentType !== 1
    const isInstallment3x = formValue.installments === 3

    if (isPixOrBoleto && isInstallment3x) {
      setPaymentText(installmentsMessage(formValue.paymentType))
      return
    }

    if (isPixOrBoleto && !isInstallment3x && isPaymentInstallmentEnabled) {
      setPaymentText('')
      return
    }

    if (session.paymentDeadline) {
      setPaymentText(getDeadlineMessage(session.paymentDeadline))
    } else {
      setPaymentText(noDeadlineAvailableMessage)
    }
  }, [session.paymentDeadline, formValue.paymentType, formValue.installments, scheduledDelivery])

  return (
    <StyledSelectionContainer>
      <StyledTitle noSelected={noSelected}>
        {isPayment ? 'Método de pagamento' : 'Tipo de documento fiscal'}
      </StyledTitle>
      <ClubbiToggleButtonGroup
        id={type}
        value={formValue[type]}
        hasIcon={isPayment}
        paymentLabels={getAdjustedLabels()}
        onChange={handleChange}
      />
      {showInstallments && (
        <>
          <StyledTitle noSelected={noSelectedInstallments}>Selecione o parcelamento</StyledTitle>
          <InstallmentsToggleButtonGroup
            value={formValue.installments}
            totalValue={totalValue}
            installmentOptions={installmentOptions}
            isInstantPayment={session.isInstantPayment}
            onChange={handleChangeInstallments}
          />
        </>
      )}
      {isPayment && !noSelected && paymentText && (
        <StyledPaymentTextContainer>
          <WalletIcon />
          <span>{paymentText}</span>
        </StyledPaymentTextContainer>
      )}
      {!isPayment && hasTwoDaysProducts && (
        <StyledPaymentTextContainer>
          <img src={'img/error-outline.png'} alt="Icone de atenção" width={25} />
          <span>Você receberá mais de uma Nota Fiscal nesta compra</span>
        </StyledPaymentTextContainer>
      )}
    </StyledSelectionContainer>
  )
}
